import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { MapPin, Package, Weight, Euro, Phone, User, X, CheckCircle2, Users, MessageCircle, Send, Trash2, Truck, Calendar, Hash, ExternalLink, Clock } from "lucide-react";
import { deleteOffer, replyWhatsApp, replyEmail, clearOffers, getWaChat, markChatRead } from "@/lib/api";

function EmailQuickInfo() {
  return null;
}


function fmtTime(v) {
  try {
    let d;
    if (typeof v === "number") d = new Date(v < 1e12 ? v * 1000 : v);
    else d = new Date(v);
    if (isNaN(d.getTime())) return "";
    const today = new Date();
    const hm = d.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay ? `azi ${hm}` : `${d.toLocaleDateString("ro-RO")} ${hm}`;
  } catch (e) {
    return "";
  }
}

function ConvThread({ convKey, onSend, label, peer, unread }) {
  const qc = useQueryClient();
  const [msgs, setMsgs] = useState([]);
  const markRead = async () => {
    if (!unread) return;
    try {
      await markChatRead(convKey);
      qc.invalidateQueries({ queryKey: ["offers"] });
    } catch (e) {}
  };
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const load = async () => {
    try {
      const d = await getWaChat(convKey);
      setMsgs(d || []);
    } catch (e) {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convKey]);
  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setErr("");
    try {
      const r = await onSend(body);
      if (r && r.ok) {
        setText("");
        await load();
      } else {
        setErr((r && r.error) || "Nu s-a putut trimite");
        setTimeout(() => setErr(""), 5000);
      }
    } catch (e) {
      setErr("Eroare de rețea");
      setTimeout(() => setErr(""), 5000);
    }
    setSending(false);
  };
  return (
    <div className="space-y-2 pt-1" data-testid={`wa-chat-${convKey}`} onMouseDown={markRead} onFocus={markRead}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-green-400">
          <MessageCircle className="w-3.5 h-3.5" /> {label || "Conversație"}
          {unread > 0 && (
            <button
              onClick={markRead}
              data-testid={`chat-unread-${convKey}`}
              className="ml-1 px-2 py-0.5 rounded-full bg-green-500 text-[#0B0F17] text-xs font-extrabold animate-pulse"
              title="Marchează ca citit"
            >
              {unread} {unread === 1 ? "mesaj nou" : "mesaje noi"}
            </button>
          )}
        </div>
        {peer && (
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-200" data-testid="conv-peer">
            <User className="w-3.5 h-3.5 text-slate-400" />
            <span className="truncate max-w-[240px]">{peer}</span>
          </div>
        )}
      </div>
      <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1 rounded-lg bg-[#0B1220] border border-[#2A364F] p-2">
        {msgs.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-3">
            Încă niciun mesaj. Scrie primul mesaj mai jos — răspunsurile lui apar aici automat.
          </p>
        ) : (
          msgs.map((mm, i) => (
            <div key={i} className={"flex " + (mm.direction === "out" ? "justify-end" : "justify-start")}>
              <div
                className={
                  "max-w-[80%] rounded-lg px-2.5 py-1.5 text-sm " +
                  (mm.direction === "out" ? "bg-green-700 text-white" : "bg-slate-700 text-slate-100")
                }
              >
                <div className="whitespace-pre-wrap break-words">{mm.text}</div>
                <div className={"text-[10px] mt-0.5 " + (mm.direction === "out" ? "text-green-200" : "text-slate-400")}>
                  {fmtTime(mm.ts)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Scrie mesaj…"
          data-testid={`wa-chat-input-${convKey}`}
          className="flex-1 bg-[#0F172A] border border-[#2A364F] rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-green-600"
        />
        <button
          onClick={send}
          disabled={sending}
          data-testid={`wa-chat-send-${convKey}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-green-600 hover:bg-green-500 text-white text-sm font-bold disabled:opacity-50 transition-colors"
        >
          <Send className="w-4 h-4" /> {sending ? "…" : "Trimite"}
        </button>
      </div>
      {err && (
        <p className="text-red-400 text-xs font-semibold" data-testid={`wa-chat-err-${convKey}`}>
          ✖ {err}
        </p>
      )}
    </div>
  );
}

const SOURCE_STYLES = {
  WhatsApp: "bg-green-900/50 text-green-300 border-green-600",
  Email: "bg-blue-900/50 text-blue-300 border-blue-600",
  Teams: "bg-purple-900/50 text-purple-200 border-purple-500",
  BursaTransport: "bg-fuchsia-900/50 text-fuchsia-200 border-fuchsia-500",
  Timocom: "bg-orange-900/50 text-orange-200 border-orange-500",
  TransEU: "bg-teal-900/50 text-teal-200 border-teal-500",
  Manual: "bg-slate-800 text-slate-300 border-slate-600",
};

const SOURCE_LABEL = {
  WhatsApp: "WhatsApp",
  Email: "E-mail",
  Teams: "Teams",
  BursaTransport: "BursaTransport",
  Timocom: "Timocom",
  TransEU: "Trans.eu",
  Manual: "Manual",
};

function InfoChip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#0F172A] border border-[#2A364F] text-sm font-medium">
      <Icon className="w-4 h-4 text-slate-400" />
      {children}
    </span>
  );
}

function OfferCard({ offer, onDelete }) {
  const e = offer.extracted || {};
  const matched = offer.matched;
  const rawGmail =
    offer.source === "Email"
      ? offer.gm_msgid
        ? `https://mail.google.com/mail/u/0/#all/${offer.gm_msgid}`
        : offer.email_msgid
        ? `https://mail.google.com/mail/u/0/#search/rfc822msgid:${encodeURIComponent(
            String(offer.email_msgid).replace(/[<>]/g, "")
          )}`
        : null
      : null;
  // Force the correct Google account (user may have several logged in) via Account Chooser.
  const gmailUrl = rawGmail
    ? offer.mailbox_email
      ? `https://accounts.google.com/AccountChooser?Email=${encodeURIComponent(
          offer.mailbox_email
        )}&continue=${encodeURIComponent(rawGmail)}`
      : rawGmail
    : null;
  const waUrl =
    offer.source === "WhatsApp" && offer.sender_phone
      ? `https://wa.me/${String(offer.sender_phone).replace(/\D/g, "")}`
      : null;
  const openUrl = gmailUrl || waUrl;
  const unread = offer.unread || 0;
  return (
    <div
      data-testid={`matched-alert-card-${offer.id}`}
      className={
        "animate-slide-in rounded-xl border p-4 space-y-3 " +
        (unread > 0 ? "ring-2 ring-green-500 " : "") +
        (matched
          ? "bg-[#1E1B4B]/60 border-indigo-500 match-glow"
          : "bg-[#151C28] border-[#2A364F]")
      }
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {unread > 0 && (
            <span
              data-testid={`offer-unread-dot-${offer.id}`}
              className="flex items-center justify-center min-w-[26px] h-[26px] px-1.5 rounded-full bg-green-500 text-[#0B0F17] text-sm font-extrabold ring-4 ring-green-500/30 animate-pulse"
              title="Mesaj nou de la client"
            >
              {unread}
            </span>
          )}
          <span
            className={
              "px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide border " +
              (SOURCE_STYLES[offer.source] || SOURCE_STYLES.Manual)
            }
          >
            {SOURCE_LABEL[offer.source] || offer.source}
          </span>
          {offer.created_at && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-400" data-testid={`offer-time-${offer.id}`}>
              <Clock className="w-3.5 h-3.5" /> {fmtTime(offer.created_at)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {matched && (
            <span className="inline-flex items-center gap-1 text-xs font-bold text-indigo-300">
              <CheckCircle2 className="w-4 h-4" /> POTRIVIRE
            </span>
          )}
          {openUrl && (
            <a
              href={openUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`open-source-${offer.id}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold transition-colors"
              title={gmailUrl ? "Deschide emailul în Gmail" : "Deschide conversația în WhatsApp"}
            >
              <ExternalLink className="w-3.5 h-3.5" /> Deschide
            </a>
          )}
          <button
            data-testid={`ignore-offer-${offer.id}`}
            onClick={() => onDelete(offer.id)}
            className="text-slate-500 hover:text-red-400 transition-colors"
            title="Ignoră"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Origin: group + sender */}
      {(offer.group || offer.sender) && (
        <div
          className="flex items-center gap-1.5 text-sm text-slate-300"
          data-testid={`offer-origin-${offer.id}`}
        >
          <Users className="w-4 h-4 text-green-400 shrink-0" />
          <span className="truncate">
            {offer.group ? (
              <>
                <span className="font-semibold text-slate-200">{offer.group}</span>
                {offer.sender ? <span className="text-slate-400"> · de la {offer.sender}</span> : null}
              </>
            ) : (
              <span className="text-slate-400">de la {offer.sender}</span>
            )}
          </span>
        </div>
      )}

      {/* Matched drivers */}
      {matched && (
        <div className="space-y-1.5">
          {offer.matches.map((m) => (
            <div
              key={m.driver_id}
              className="flex items-center justify-between rounded-lg bg-indigo-950/50 border border-indigo-700 px-3 py-2"
            >
              <span className="font-display font-bold text-indigo-200">
                {m.driver_label} · {m.driver_name}
              </span>
              <span className="text-xs font-mono text-indigo-300">
                {m.driver_postal} · {m.distance_km} km
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Route */}
      <div className="space-y-1.5" data-testid={`offer-route-${offer.id}`}>
        <div className="flex items-center gap-2 text-base font-semibold">
          <MapPin className="w-4 h-4 text-green-400 shrink-0" />
          <span className="font-mono">{e.load_postal || "?"}</span>
          <span className="text-slate-400 truncate text-sm">
            {[e.load_city, e.load_country].filter(Boolean).join(", ")}
          </span>
          <span className="text-slate-500">→</span>
          <MapPin className="w-4 h-4 text-orange-400 shrink-0" />
          <span className="font-mono">{e.unload_postal || "?"}</span>
          <span className="text-slate-400 truncate text-sm">
            {[e.unload_city, e.unload_country].filter(Boolean).join(", ")}
          </span>
        </div>
        {(e.load_date || e.unload_date) && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400 pl-6">
            {e.load_date && (
              <span className="inline-flex items-center gap-1" data-testid={`offer-load-date-${offer.id}`}>
                <Calendar className="w-3.5 h-3.5 text-green-500" /> Încărcare: {e.load_date}
              </span>
            )}
            {e.unload_date && (
              <span className="inline-flex items-center gap-1" data-testid={`offer-unload-date-${offer.id}`}>
                <Calendar className="w-3.5 h-3.5 text-orange-500" /> Descărcare: {e.unload_date}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex flex-wrap gap-2">
        <InfoChip icon={Package}>{e.pallets ?? "?"} paleți</InfoChip>
        <InfoChip icon={Weight}>{e.weight_kg ?? "?"} kg</InfoChip>
        {e.vehicle_type && <InfoChip icon={Truck}>{e.vehicle_type}</InfoChip>}
        {e.price && <InfoChip icon={Euro}>{e.price}</InfoChip>}
        {e.reference && <InfoChip icon={Hash}>{e.reference}</InfoChip>}
        {e.contact_phone && <InfoChip icon={Phone}>{e.contact_phone}</InfoChip>}
        {e.contact_person && <InfoChip icon={User}>{e.contact_person}</InfoChip>}
      </div>

      {/* Two-way private conversation with the sender (history + live replies) */}
      {offer.source === "WhatsApp" && (offer.sender_phone || offer.sender_jid) && (
        <ConvThread
          convKey={offer.sender_phone ? String(offer.sender_phone).replace(/\D/g, "") : offer.sender_jid}
          onSend={(t) => replyWhatsApp(offer.sender_jid, t)}
          label="Conversație WhatsApp"
          unread={unread}
          peer={[offer.sender, offer.sender_phone && "+" + String(offer.sender_phone).replace(/\D/g, "")].filter(Boolean).join(" · ")}
        />
      )}
      {/* Reply via email to the sender */}
      {offer.source === "Email" && offer.email_from && (
        <ConvThread
          convKey={offer.id}
          onSend={(t) => replyEmail(offer.id, t)}
          label="Conversație E-mail"
          unread={unread}
          peer={[(offer.sender || "").replace(/<[^>]*>/, "").trim() || null, offer.email_from].filter(Boolean).join(" · ")}
        />
      )}
    </div>
  );
}

export default function FeedPanel({ offers, radius }) {
  const qc = useQueryClient();
  const delMut = useMutation({
    mutationFn: (id) => deleteOffer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["offers"] }),
  });
  const clearMut = useMutation({
    mutationFn: (onlyMatched) => clearOffers(onlyMatched),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["offers"] }),
  });

  const matched = offers.filter((o) => o.matched);

  const Empty = ({ text }) => (
    <div className="text-center py-10 text-slate-500 text-sm">{text}</div>
  );

  const ClearButton = ({ onlyMatched, count }) => (
    <button
      onClick={() => {
        if (window.confirm(onlyMatched ? "Ștergi toate alertele?" : "Ștergi toate ofertele?")) {
          clearMut.mutate(onlyMatched);
        }
      }}
      disabled={clearMut.isPending || count === 0}
      data-testid={onlyMatched ? "clear-matches-btn" : "clear-all-btn"}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-900/40 border border-red-700 text-red-300 text-sm font-semibold hover:bg-red-900/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <Trash2 className="w-4 h-4" /> Șterge tot
    </button>
  );

  return (
    <div className="rounded-2xl border border-[#2A364F] bg-[#151C28] p-5">
      <Tabs defaultValue="matches">
        <TabsList className="grid grid-cols-2 bg-[#0F172A] h-11">
          <TabsTrigger data-testid="tab-matches" value="matches" className="text-base font-semibold">
            🔔 Alerte ({matched.length})
          </TabsTrigger>
          <TabsTrigger data-testid="tab-all" value="all" className="text-base font-semibold">
            Toate ({offers.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="matches" className="mt-4 space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          {matched.length === 0 ? (
            <Empty text="Nicio potrivire încă. Ofertele care se potrivesc cu o dubă liberă apar aici." />
          ) : (
            <>
              <div className="flex justify-end">
                <ClearButton onlyMatched count={matched.length} />
              </div>
              {matched.map((o) => (
                <OfferCard key={o.id} offer={o} onDelete={delMut.mutate} />
              ))}
            </>
          )}
        </TabsContent>

        <TabsContent value="all" className="mt-4 space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          {offers.length === 0 ? (
            <Empty text="Nicio ofertă procesată încă." />
          ) : (
            <>
              <div className="flex justify-end">
                <ClearButton onlyMatched={false} count={offers.length} />
              </div>
              {offers.map((o) => (
                <OfferCard key={o.id} offer={o} onDelete={delMut.mutate} />
              ))}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
